import { useState } from "react";
import { usePermissions } from "@/data/PermissionsContext";
import { useBranding } from "@/data/BrandingContext";
import type { ContractDocument } from "@/data/quoteTypes";
import { supabase } from "@/lib/supabase";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  FileText, Download, Eye, CheckCircle, Lock, Shield,
} from "lucide-react";

/** Parse a contract filePath ("bucket::path" or full Supabase URL) into its bucket and path components. */
function parseContractFilePath(rawPath?: string | null): { bucket: string; path: string } | null {
  const value = String(rawPath ?? "").trim();
  if (!value) return null;
  if (value.includes("::")) {
    const [bucket, path] = value.split("::", 2) as [string, string];
    return bucket && path ? { bucket, path } : null;
  }
  // Handle full Supabase storage URLs (public or signed)
  const match = value.match(/\/storage\/v1\/object\/(?:public|sign)\/([^/?#]+)\/([^?#]+)/);
  if (match) {
    const bucket = match[1] ?? "";
    const path = (match[2] ?? "").split("?")[0];
    return bucket && path ? { bucket, path } : null;
  }
  return null;
}

interface Props {
  contract?: ContractDocument;
  compact?: boolean;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CTR_UUID_RE = /^CTR-([0-9a-f]{8})-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Returns a display-friendly contract ID, hiding raw UUIDs. */
function displayContractId(id: string): string {
  const ctrMatch = id.match(CTR_UUID_RE);
  if (ctrMatch) return `CTR-${ctrMatch[1]!.toUpperCase()}`;
  if (UUID_RE.test(id)) return `CTR-${id.slice(0, 8).toUpperCase()}`;
  return id;
}

export function ContractDocumentView({ contract, compact }: Props) {
  const { hasPermission: hasPerm } = usePermissions();
  const { branding } = useBranding();
  const [viewOpen, setViewOpen] = useState(false);
  const [openingDoc, setOpeningDoc] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const canDownload = hasPerm("reports_export");

  const handleViewDocument = async () => {
    if (!contract) return;
    const parsed = parseContractFilePath(contract.filePath);
    if (!parsed) {
      setViewOpen(true);
      return;
    }
    setOpeningDoc(true);
    try {
      const { data, error } = await supabase.storage
        .from(parsed.bucket)
        .createSignedUrl(parsed.path, 60 * 60);
      if (error || !data?.signedUrl) throw error ?? new Error("No signed URL returned");
      setPdfUrl(data.signedUrl);
    } catch (err) {
      toast.error("Could not open document", {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setOpeningDoc(false);
    }
  };

  if (!contract) {
    if (compact) return null;
    return (
      <Card className="shadow-none border-dashed">
        <CardContent className="p-4 text-center">
          <FileText className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">No contract generated yet</p>
        </CardContent>
      </Card>
    );
  }

  const statusColor = contract.status === "Signed"
    ? "bg-success/10 text-success border-success/30"
    : contract.status === "Pending"
    ? "bg-warning/10 text-warning border-warning/30"
    : "bg-muted text-muted-foreground border-border";

  if (compact) {
    return (
      <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          <div>
            <p className="text-xs font-semibold">{displayContractId(contract.id)}</p>
            <p className="text-[10px] text-muted-foreground">
              {contract.status === "Signed" ? `Signed ${new Date(contract.approvedAt!).toLocaleDateString()}` : contract.status}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className={`text-[9px] ${statusColor}`}>
            {contract.status === "Signed" && <CheckCircle className="h-2.5 w-2.5 mr-0.5" />}
            {contract.status}
          </Badge>
          <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={() => void handleViewDocument()} disabled={openingDoc}>
            <Eye className="h-3 w-3" /> {openingDoc ? "Opening…" : "View"}
          </Button>
        </div>
        <ContractViewDialog contract={contract} open={viewOpen} onOpenChange={setViewOpen} canDownload={canDownload} branding={branding} />
        <PdfViewerDialog url={pdfUrl} onClose={() => setPdfUrl(null)} canDownload={canDownload} />
      </div>
    );
  }

  return (
    <Card className="shadow-none">
      <CardContent className="p-4">
        <div className="flex items-center justify-between pb-1.5 mb-3 border-b">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Contract Document</h2>
          </div>
          <Badge variant="outline" className={`text-[9px] ${statusColor}`}>
            {contract.status === "Signed" && <CheckCircle className="h-2.5 w-2.5 mr-0.5" />}
            {contract.status}
          </Badge>
        </div>

        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Contract ID</span>
            <span className="font-medium font-mono">{displayContractId(contract.id)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Customer</span>
            <span className="font-medium">{contract.customerName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total</span>
            <span className="font-bold text-primary">${contract.contractTotal.toLocaleString()}</span>
          </div>
          {contract.approvedAt && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Signed</span>
              <span>{new Date(contract.approvedAt).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {/* Signatures preview */}
        {contract.homeownerSignature && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Signatures</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] text-muted-foreground mb-1">Homeowner</p>
                <div className="rounded border bg-card p-1 h-14">
                  <img src={contract.homeownerSignature} alt="Homeowner signature" className="h-full w-full object-contain" />
                </div>
                <p className="text-[9px] text-muted-foreground mt-0.5">{new Date(contract.homeownerSignedAt!).toLocaleString()}</p>
              </div>
              {contract.repSignature && (
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Representative</p>
                  <div className="rounded border bg-card p-1 h-14">
                    <img src={contract.repSignature} alt="Rep signature" className="h-full w-full object-contain" />
                  </div>
                  <p className="text-[9px] text-muted-foreground mt-0.5">{new Date(contract.repSignedAt!).toLocaleString()}</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mt-3 pt-3 border-t">
          <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 flex-1" onClick={() => void handleViewDocument()} disabled={openingDoc}>
            <Eye className="h-3 w-3" /> {openingDoc ? "Opening…" : contract.filePath ? "Open Document" : "View Full Document"}
          </Button>
          {canDownload ? (
            <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={() => toast("Contract download started")}>
              <Download className="h-3 w-3" /> Download
            </Button>
          ) : (
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Lock className="h-3 w-3" /> Download restricted
            </div>
          )}
        </div>
      </CardContent>
      <ContractViewDialog contract={contract} open={viewOpen} onOpenChange={setViewOpen} canDownload={canDownload} branding={branding} />
      <PdfViewerDialog url={pdfUrl} onClose={() => setPdfUrl(null)} canDownload={canDownload} />
    </Card>
  );
}

function PdfViewerDialog({ url, onClose, canDownload }: {
  url: string | null;
  onClose: () => void;
  canDownload: boolean;
}) {
  return (
    <Dialog open={!!url} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-4xl h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
          <DialogTitle className="flex items-center gap-2 text-sm">
            <FileText className="h-4 w-4 text-primary" /> Contract Document
          </DialogTitle>
          {canDownload && url && (
            <a href={url} download className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors">
              <Download className="h-3.5 w-3.5" /> Download
            </a>
          )}
        </DialogHeader>
        {url && (
          <iframe
            src={url}
            className="flex-1 w-full border-0"
            title="Contract Document"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ContractViewDialog({ contract, open, onOpenChange, canDownload, branding }: {
  contract: ContractDocument;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  canDownload: boolean;
  branding: { companyName: string; platformName: string };
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" /> Contract — {displayContractId(contract.id)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Header */}
          <div className="text-center border-b pb-4">
            <h2 className="text-lg font-bold">{branding.companyName}</h2>
            <p className="text-xs text-muted-foreground">Installation Agreement</p>
          </div>

          {/* Details */}
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="space-y-1.5">
              <p className="text-muted-foreground text-[10px] uppercase tracking-wider font-semibold">Customer</p>
              <p className="font-medium">{contract.customerName}</p>
              {contract.customerEmail && <p className="text-muted-foreground">{contract.customerEmail}</p>}
              {contract.customerAddress && <p className="text-muted-foreground">{contract.customerAddress}</p>}
            </div>
            <div className="space-y-1.5 text-right">
              <p className="text-muted-foreground text-[10px] uppercase tracking-wider font-semibold">Agreement</p>
              <p className="font-mono font-medium">{displayContractId(contract.id)}</p>
              <p className="text-muted-foreground">Date: {new Date(contract.createdAt).toLocaleDateString()}</p>
            </div>
          </div>

          {/* Total */}
          <div className="rounded-lg bg-primary/5 border border-primary/20 p-4 text-center">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Contract Total</p>
            <p className="text-2xl font-bold text-primary">${contract.contractTotal.toLocaleString()}</p>
          </div>

          {/* Terms */}
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3 text-success" />
              <span>Terms and conditions accepted</span>
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3 text-success" />
              <span>3-business-day right of rescission acknowledged</span>
            </div>
          </div>

          {/* Signatures */}
          <div className="border-t pt-4">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Signatures</p>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="rounded border bg-card p-2 h-20">
                  {contract.homeownerSignature && (
                    <img src={contract.homeownerSignature} alt="Homeowner" className="h-full w-full object-contain" />
                  )}
                </div>
                <div className="border-t mt-1 pt-1">
                  <p className="text-[10px] font-medium">{contract.customerName}</p>
                  <p className="text-[9px] text-muted-foreground">
                    {contract.homeownerSignedAt && new Date(contract.homeownerSignedAt).toLocaleString()}
                  </p>
                </div>
              </div>
              <div>
                <div className="rounded border bg-card p-2 h-20">
                  {contract.repSignature && (
                    <img src={contract.repSignature} alt="Representative" className="h-full w-full object-contain" />
                  )}
                </div>
                <div className="border-t mt-1 pt-1">
                  <p className="text-[10px] font-medium">Company Representative</p>
                  <p className="text-[9px] text-muted-foreground">
                    {contract.repSignedAt && new Date(contract.repSignedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t text-[10px] text-muted-foreground">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <Shield className="h-3 w-3" />
                <span>Securely stored · View-only access</span>
              </div>
              <span className="text-muted-foreground/40">Powered by {branding.platformName}</span>
            </div>
            {canDownload ? (
              <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1">
                <Download className="h-3 w-3" /> Download PDF
              </Button>
            ) : (
              <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> Download restricted</span>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Need toast import
import { toast } from "sonner";
